import { reactive, watch, readonly } from "vue";

/**
 * Manages row selection state and logic.
 * * @param {import('vue').Ref<Array<string|number>>} selectedRowsProp - A Ref to the v-model prop (e.g., toRef(props, 'selectedRows')). This should be an array of IDs.
 * @param selectedRowsProp
 * @param {Function} emit - The component's emit function from defineEmits().
 */
export function useRowSelection(selectedRowsProp, emit) {
  const sanitizeSelectedRows = (selectedRows) => {
    if (!Array.isArray(selectedRows)) {
      return [];
    }

    return selectedRows.filter((id) => id !== undefined && id !== null);
  };

  const resolveRowId = (row, key) => {
    if (!row || typeof row !== "object") {
      return undefined;
    }

    return row[key];
  };

  // 1. Internal State (replaces `data`)
  // We use a reactive Set for efficient add/delete/has operations.
  // It's initialized from the prop's starting value.
  const selectedRowsLocal = reactive(
    new Set(sanitizeSelectedRows(selectedRowsProp.value)),
  );

  // 2. Watcher (replaces `watch`)
  // Syncs the internal Set when the prop changes from the parent.
  watch(selectedRowsProp, (newPropValue) => {
    // Re-sync the local Set with the new array of IDs from the prop.
    // This is the most direct translation of the mixin's watch logic.
    selectedRowsLocal.clear();
    sanitizeSelectedRows(newPropValue).forEach((id) => {
      selectedRowsLocal.add(id);
    });
  });

  // 3. Emitter (replaces `emitSelection` method)
  /**
   * Emits the current selection (as an array of IDs) to the parent.
   */
  const emitSelection = () => {
    const selectedArray = Array.from(selectedRowsLocal);
    emit("update:selectedRows", selectedArray);
  };

  // 4. Methods (replaces `methods`)
  /**
   * Toggles row selection (selects if not selected, deselects if selected).
   * @param {object} row - The full row object.
   * @param {string} [key="id"] - The key on the row object to use as the unique ID.
   */
  const toggleRowSelection = (row, key = "id") => {
    const rowId = resolveRowId(row, key);

    if (rowId === undefined || rowId === null) {
      return;
    }

    if (selectedRowsLocal.has(rowId)) {
      selectedRowsLocal.delete(rowId);
    } else {
      selectedRowsLocal.add(rowId);
    }
    emitSelection();
  };

  /**
   * Checks if a row is selected.
   * @param {object} row - The full row object.
   * @param {string} [key="id"] - The key on the row object to use as the unique ID.
   */
  const isRowSelected = (row, key = "id") => {
    const rowId = resolveRowId(row, key);

    if (rowId === undefined || rowId === null) {
      return false;
    }

    return selectedRowsLocal.has(rowId);
  };

  /**
   * Selects all given rows.
   * @param {Array<object>} rows - An array of the full row objects to select.
   * @param {string} [key="id"] - The key on the row object to use as the unique ID.
   */
  const selectAll = (rows, key = "id") => {
    selectedRowsLocal.clear();

    rows.forEach((row) => {
      const rowId = resolveRowId(row, key);
      if (rowId !== undefined && rowId !== null) {
        selectedRowsLocal.add(rowId);
      }
    });

    emitSelection();
  };

  /**
   * Clears all selected rows.
   */
  const clearSelection = () => {
    selectedRowsLocal.clear();
    emitSelection();
  };

  // 5. Return (exposes state and methods to the component)
  return {
    // We return a readonly version of the state to prevent accidental
    // mutation from the component. State should only be changed
    // via the returned methods.
    selectedRowsLocal: readonly(selectedRowsLocal),

    // Return all the methods
    toggleRowSelection,
    isRowSelected,
    selectAll,
    clearSelection,
  };
}
